import { lowerCamelCase, object } from '@art-suite/art-core-ts';
import { z } from 'zod';

export type NamedFeatureFlagProps = {
  description: string;
};

/**
 * Registry of all known feature flags: name → description.
 * Add new flags here. Everything else is derived from this.
 */
export const NAMED_FEATURE_FLAGS = {
  newScopeBuilderFlow: {
    description: 'Use the next-generation scope builder scan page at /scan/:id',
  },
  turnBasedHandoff: {
    description: 'Enable the draft scope handoff workflow for moving responsibility between team members',
  },
  modifierSettings: {
    description: 'Enable the Org Settings Modifiers page, and hide the legacy Outputs Tagging toggles it replaces',
  },
  apiKeys: {
    description: 'Show the API Keys tab in Org Settings',
  },
  useRoomMeasurements: {
    description: 'Use new room measurements schema and logic',
  },
  useAiVideoScan: {
    description: 'Enable AI video scan upload and processing flow',
  },
  mobileVideoIssueLanding: {
    description: 'Show the issue video upload CTA and route on the mobile landing page',
  },
  contractorOnboarding: {
    description: 'Enable the contractor onboarding flow: copyable contractor links on the project contractors panel',
  },
  floorPlanEditorAdvanced: {
    description:
      "Enable the floor plan editors' advanced tools: the floor plan view's whole-plan JSON download and copy, and the floor plan editor demo in the account menu",
  },
  measuredLineItems: {
    description: 'Enable authoring measurement-driven line items (measured/overage/coverage) in the template editor',
  },
  fullFloorPlans: {
    description: 'Enable the floor plan assembler: position rooms relative to each other and switch between levels in the scope-level Floor plan panel',
  },
  scopeTimeShopping: {
    description: 'Enable the "Allow product selection during scoping" toggle on line items in the template editor',
  },
  kaiProRealtimeAiScoping: {
    description: 'Enable realtime AI scoping in the Kai Pro iPad app',
  },
  testingTools: {
    description: "Internal-only testing controls, such as editing a photo's date from the photo gallery",
  },
  // Supplier and catalog flags share the "supplier" prefix so they group together.
  supplierCatalogV2: {
    description: 'Route Home Depot catalog reads to the catalog v2 tables instead of the legacy tables and use multiple suppliers',
  },
  supplierSearch: {
    description: 'Serve supplier catalog product search from Typesense instead of the warehouse search (needs supplierCatalogV2)',
  },
  supplierDaikinMotiliCatalog: {
    description: 'Expose Daikin/Motili as a supplier: its catalog products and supplier access settings (needs supplierCatalogV2)',
  },
  supplierHdSupplyCatalog: {
    description: 'Expose HD Supply as a supplier: its catalog products and supplier access settings (needs supplierCatalogV2)',
  },
  supplierLowesCatalog: {
    description: "Expose Lowe's catalog products and Lowe's SKU validation (needs supplierCatalogV2)",
  },
  supplierWayfairCatalog: {
    description: 'Expose Wayfair as a supplier: its catalog products and supplier access settings (needs supplierCatalogV2)',
  },
  taskActivity: {
    description: "Show a task's full activity log: every edit to it, its options and line items, grouped by scope version",
  },
} as const satisfies Record<string, NamedFeatureFlagProps>;

// *************************************************************************************
// Derived Feature Flag Constants
// *************************************************************************************
export const DEFAULT_NAMED_FEATURE_FLAGS = object(
  NAMED_FEATURE_FLAGS,
  v => false,
) as unknown as Record<FeatureFlagName, boolean>;
export const FEATURE_FLAG_NAMES = Object.keys(
  NAMED_FEATURE_FLAGS,
) as FeatureFlagName[];

export const isFeatureFlagName = (name: string): name is FeatureFlagName => name in NAMED_FEATURE_FLAGS;

export type FeatureFlagName = keyof typeof NAMED_FEATURE_FLAGS;
export const FeatureFlagNameSchema = z.enum(
  FEATURE_FLAG_NAMES as [FeatureFlagName, ...FeatureFlagName[]],
);

export type NamedFeatureFlags = Record<FeatureFlagName, boolean>;
export const normalizeFeatureFlagName = (name: string): FeatureFlagName => lowerCamelCase(name) as FeatureFlagName;
